import { useQuery } from '@tanstack/react-query';

import { ENDPOINT } from '~/constants/endpoints';

import { type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { queryKeysUser } from './queryKeys';

type UserType =
  | 'buyer'
  | 'carrier'
  | 'recipient'
  | 'supplier'
  | 'on_behalf_buyer'
  | 'on_behalf_carrier'
  | 'on_behalf_recipient'
  | 'on_behalf_seller'
  | 'on_behalf_supplier';

type LocalStorageBackup = {
  data: Record<string, string>;
  timestamp: string;
  version: number;
};

type UserSettings = {
  costCenter: UUID[];
  currentVehicles: [
    {
      lastUsed: string;
      vehicleId: UUID;
    },
  ];
  defaultCostCenter: string;
  defaultSignatureRole: UserType;
  deviceTokens: [
    {
      token: string;
      lastUsed: string;
      createdOn: string;
    },
  ];
  firstName: string;
  language: 'de';
  lastName: string;
  lastUsedVehicles: [
    {
      lastUsed: string;
      vehicleId: UUID;
    },
  ];
  licensePlates: string[];
  localStorageBackup?: LocalStorageBackup;
  permittedSignatureRoles: UserType[];
  permittedUserTypes: UserType[];
  position: string;
  userType: UserType;
};

/**
 * Fetches user settings data from the API.
 *
 * @param {string} userId - The ID of the user to fetch settings for.
 * @returns {Promise<UserSettings>} A promise that resolves with the user settings.
 * @see https://app.dev.vestigas.com/redoc#tag/Admin/operation/get_settings_for_user_admin_user__id__settings_get
 */
export const fetchUserSettings = async (userId: UUID) => {
  const response = await vestigasApi
    .get(ENDPOINT.USER.GET_SETTINGS(userId))
    .json<UserSettings>();

  return response;
};

/**
 * Custom hook for fetching user settings.
 *
 * @param {string} userId - The ID of the user.
 * @param {Object} options - Additional options for the query.
 * @returns {Object} The query result.
 */
export const useQueryUserSettings = (
  userId: UUID,
  options: Parameters<typeof useQuery>[0],
) =>
  useQuery({
    queryFn: async () => fetchUserSettings(userId),
    queryKey: queryKeysUser.getSettings(userId),
    ...options,
  });
